// ── threshold-editor-row: one threshold card (read-only OR inline edit) ─────
// SA/Admin get an inline edit (per-threshold value) that POSTs the upsert with a
// generated UUID Idempotency-Key (in the hook) and toasts ONLY after the backend
// confirms; a non-editor sees the same card read-only. A field with no stored row
// shows a neutral "not set" placeholder — no value is fabricated. Design tokens
// only (no raw hex); @vjal/ui primitives.

import { Alert, Badge, Button, Input, useToast } from "@vjal/ui";
import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";

import { useThresholdUpsertMutation } from "../../hooks/useCoachingSettings";
import { describeThresholdWriteError } from "./thresholds-write";
import { clampThresholdPercent, isValidThresholdPercent, type MergedThresholdField } from "./thresholds-helpers";

export function ThresholdEditorRow({ field, editable }: { field: MergedThresholdField; editable: boolean }) {
  const toast = useToast();
  const upsert = useThresholdUpsertMutation();
  const storedValue = field.stored ? String(field.stored.value) : "";
  const [draft, setDraft] = useState<string>(storedValue);
  const writeError = describeThresholdWriteError(upsert.error);

  const parsed = Number(draft);
  const draftValid = draft.trim().length > 0 && isValidThresholdPercent(parsed);
  const dirty = draft.trim() !== storedValue.trim();

  function save() {
    if (!draftValid) return;
    upsert.reset();
    upsert.mutate(
      { key: field.key, value: clampThresholdPercent(parsed) },
      { onSuccess: () => toast.success(`${field.label} saved`) }
    );
  }

  return (
    <div className="rounded-lg border border-line bg-white p-4" data-testid={`threshold-${field.key}`}>
      <label className="text-sm font-semibold text-strong" htmlFor={`threshold-${field.key}`}>
        {field.label}
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Input
          id={`threshold-${field.key}`}
          type="number"
          min={0}
          max={100}
          // Read-only roles see the value (or placeholder); editors edit a draft.
          value={editable ? draft : storedValue}
          placeholder={field.placeholder}
          disabled={!editable || upsert.isPending}
          onChange={editable ? (event) => setDraft(event.target.value) : undefined}
          aria-label={editable ? `${field.label} (percentage)` : `${field.label} (view only)`}
        />
        <span className="text-sm text-ink-muted">%</span>
      </div>
      <p className="m-0 mt-2 text-sm text-ink-muted">{field.description}</p>

      {field.stored ? (
        <Badge variant="default" className="mt-2">
          Stored: {field.stored.value}% · {field.stored.status}
        </Badge>
      ) : (
        <Badge variant="default" className="mt-2">
          Not set
        </Badge>
      )}

      {editable ? (
        <div className="mt-3 space-y-2">
          <Button
            type="button"
            variant="secondary"
            leftIcon={<SlidersHorizontal size={16} />}
            disabled={!draftValid || !dirty}
            loading={upsert.isPending}
            onClick={save}
            aria-label={`Save ${field.label}`}
          >
            Save
          </Button>
          {!draftValid && draft.trim().length > 0 ? (
            <Alert variant="warning">Enter a whole percentage between 0 and 100.</Alert>
          ) : null}
          {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}
        </div>
      ) : null}
    </div>
  );
}
